import { Injectable } from "@angular/core";
import QRCode from "qrcode";
import { LoadingService } from "src/services/loading/loading.service";
import { MessageService } from "src/services/message/message.service";
import { isNative } from "src/services/utils/runtime.utils";
import { LabelContent } from "src/services/qr-code/label-content.interface";
import {
  LabelFormat,
  LabelFormatName,
  LabelGeometry,
  LabelLayout,
  labelFormats,
  resolveLabelGeometry,
} from "src/services/qr-code/label-format.const";

const previewQrPixels = 512;
const quietZoneModules = 4;

@Injectable({ providedIn: "root" })
export class LabelPrintingService {
  public readonly isAvailable = !isNative();

  private readonly logoDataUrls = new Map<string, string>();
  private openFrame: HTMLIFrameElement | undefined;

  constructor(
    private readonly loadingService: LoadingService,
    private readonly messageService: MessageService,
  ) {}

  public async print(labels: LabelContent[], formatName: LabelFormatName): Promise<void> {
    if (!labels?.length) {
      await this.messageService.presentErrorToast("There is nothing to print.");
      return;
    }

    if (!this.isAvailable) {
      await this.messageService.presentErrorToast("Printing is only available in the web app.");
      return;
    }

    try {
      const html = await this.loadingService.wrapInLoading(
        async () => this.buildPrintDocument(labels, formatName),
        "Preparing labels…",
      );

      await this.printDocument(html);
    } catch (error) {
      await this.messageService.presentUnexpectedErrorToast("The labels could not be printed.", error);
    }
  }

  public async buildPrintDocument(labels: LabelContent[], formatName: LabelFormatName): Promise<string> {
    const format = labelFormats[formatName];
    const geometry = resolveLabelGeometry(format);
    const logoDataUrl = await this.getLogoDataUrl(format.logoAsset);

    const renderedLabels = await Promise.all(
      labels.map(async (label) => {
        const qrDataUrl = await this.renderQrDataUrl(label.qrPayload, geometry.qrPixels);

        return this.buildLabelMarkup(label, format, qrDataUrl, logoDataUrl);
      }),
    );

    return this.buildDocument(format, geometry, renderedLabels.join(""));
  }

  public async renderPreviewDataUrl(qrPayload: string): Promise<string> {
    return this.renderQrDataUrl(qrPayload, previewQrPixels);
  }

  private async renderQrDataUrl(qrPayload: string, targetPixels: number): Promise<string> {
    const options = { errorCorrectionLevel: "M" as const, margin: quietZoneModules };
    const moduleCount = QRCode.create(qrPayload, options).modules.size + quietZoneModules * 2;
    const scale = Math.max(1, Math.round(targetPixels / moduleCount));

    return QRCode.toDataURL(qrPayload, { ...options, width: moduleCount * scale });
  }

  private buildLabelMarkup(label: LabelContent, format: LabelFormat, qrDataUrl: string, logoDataUrl: string): string {
    const lines = label.lines.map((line) => `<p class="label__line">${escapeHtml(line)}</p>`).join("");
    const footer = label.footer ? `<p class="label__footer">${escapeHtml(label.footer)}</p>` : "";
    const layoutModifier = format.layout === LabelLayout.stacked ? " label--stacked" : "";

    return `<section class="label${layoutModifier}">
      <div class="label__text">
        <img class="label__logo" src="${logoDataUrl}" alt="" />
        ${lines}
        ${footer}
      </div>
      <img class="label__qr" src="${qrDataUrl}" alt="" />
    </section>`;
  }

  private buildDocument(format: LabelFormat, geometry: LabelGeometry, body: string): string {
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Rocket Closet Labels</title>
    <style>
      @page {
        size: ${format.widthMm}mm ${format.heightMm}mm;
        margin: 0;
      }

      * {
        box-sizing: border-box;
      }

      html,
      body {
        margin: 0;
        padding: 0;
      }

      .label {
        align-items: center;
        break-after: page;
        display: flex;
        font-family: Arial, Helvetica, sans-serif;
        gap: ${geometry.gapMm}mm;
        height: ${geometry.boxHeightMm}mm;
        justify-content: space-between;
        overflow: hidden;
        padding: ${geometry.paddingMm}mm;
        page-break-after: always;
        width: ${geometry.boxWidthMm}mm;
      }

      .label:last-child {
        break-after: auto;
        page-break-after: auto;
      }

      .label--stacked {
        flex-direction: column;
        justify-content: center;
      }

      .label__text {
        align-items: center;
        display: flex;
        flex-direction: column;
        gap: ${geometry.fontSizeMm * 0.3}mm;
        justify-content: center;
        width: ${geometry.textWidthMm}mm;
      }

      .label__logo {
        height: ${geometry.logoHeightMm}mm;
        max-width: 100%;
        object-fit: contain;
        width: auto;
      }

      .label__line,
      .label__footer {
        font-size: ${geometry.fontSizeMm}mm;
        line-height: 1.15;
        margin: 0;
        overflow-wrap: anywhere;
        text-align: center;
      }

      .label__footer {
        font-size: ${geometry.footerSizeMm}mm;
      }

      .label__qr {
        flex-shrink: 0;
        height: ${geometry.qrSizeMm}mm;
        image-rendering: pixelated;
        width: ${geometry.qrSizeMm}mm;
      }
    </style>
  </head>
  <body>${body}</body>
</html>`;
  }

  private async printDocument(html: string): Promise<void> {
    this.openFrame?.remove();

    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.border = "0";
    frame.style.height = "297mm";
    frame.style.left = "-10000px";
    frame.style.position = "fixed";
    frame.style.top = "0";
    frame.style.width = "210mm";
    document.body.appendChild(frame);

    const frameDocument = frame.contentDocument;
    const frameWindow = frame.contentWindow;
    if (!frameDocument || !frameWindow) {
      frame.remove();
      throw new Error("The print view could not be created.");
    }

    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    await waitForImages(frameDocument);

    this.openFrame = frame;
    frameWindow.addEventListener(
      "afterprint",
      () => {
        if (this.openFrame === frame) {
          this.openFrame = undefined;
        }
        frame.remove();
      },
      { once: true },
    );

    frameWindow.focus();
    frameWindow.print();
  }

  private async getLogoDataUrl(assetPath: string): Promise<string> {
    const cached = this.logoDataUrls.get(assetPath);
    if (cached) {
      return cached;
    }

    const response = await fetch(assetPath);
    if (!response.ok) {
      throw new Error(`The label logo could not be loaded (${response.status}).`);
    }

    const blob = await response.blob();
    const dataUrl = await blobToDataUrl(blob);
    this.logoDataUrls.set(assetPath, dataUrl);

    return dataUrl;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

async function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

async function waitForImages(frameDocument: Document): Promise<void> {
  await Promise.all(
    Array.from(frameDocument.images).map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.addEventListener("load", () => resolve(), { once: true });
          image.addEventListener("error", () => resolve(), { once: true });
        }),
    ),
  );
}
